import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import { ANALYTICS_EVENTS } from '../constants/analytics';

// Matches React.lazy's own signature: T extends ComponentType<any> covers
// both function and class components with arbitrary prop shapes. `any` here
// is intentional and required for compatibility with React.lazy.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

const RELOAD_KEY_PREFIX = 'chunk-reload:';
const RELOAD_COUNT_KEY = 'chunk-reload-count';
// Session-wide ceiling across ALL chunks — the belt against a pathological
// deploy state (e.g. a CDN serving a permanently stale index.html) turning
// per-chunk one-shot reloads into a reload storm.
const MAX_RELOADS_PER_SESSION = 3;

/**
 * Stable per-chunk guard key. The failed module URL (when the engine's
 * message includes one) contains the content hash, so:
 *  - the SAME stale URL failing after a reload → key present → give up
 *    (index.html is still stale; rethrow to the ErrorBoundary), while
 *  - a DIFFERENT chunk failing later in the session still gets its own
 *    one-shot reload (the old global flag denied it one).
 */
function chunkKeyFor(error: Error): string {
  const url = error.message.match(/[a-z]+:\/\/\S+/i)?.[0];
  // WebKit's "Importing a module script failed." carries no URL, so all
  // Safari chunk failures share one message-based key — i.e. Safari gets
  // exactly one stale-chunk reload per tab-session across ALL chunks.
  // Deliberate degradation: still recovers the common single-stale-deploy
  // case without risking cross-chunk reload chains.
  return RELOAD_KEY_PREFIX + (url ?? error.message.slice(0, 120));
}

/**
 * How many stale-chunk reloads this tab-session has already spent. Shared with
 * the ErrorBoundary so its manual reload can report the same number the
 * automatic path does — without it the two halves of `STALE_CHUNK_RELOADED`
 * carry disjoint properties and cannot be compared.
 */
export function staleChunkReloadCount(): number {
  // Reading must not throw. `ErrorBoundary.componentDidCatch` calls this to
  // classify the hit it is about to log, and an exception raised inside a
  // boundary's own lifecycle escalates to the NEXT boundary up — at app level
  // that is an unmounted tree and a white screen, in place of the reload
  // prompt this function exists to support. `sessionStorage` access itself
  // throws (SecurityError) in a webview with storage disabled.
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(RELOAD_COUNT_KEY);
  } catch {
    return 0;
  }
  // NaN-guard: a corrupted count would make `NaN >= cap` false and silently
  // disable the session ceiling.
  const stored = Number(raw ?? '0');
  return Number.isFinite(stored) ? stored : 0;
}

/**
 * Take this chunk's one-shot reload, atomically as far as callers are
 * concerned: false means somebody already had it, or storage refused.
 *
 * Refusal has to mean "don't reload", not "reload anyway". The one-shot IS the
 * loop guard, so an auto-reload we cannot record is an auto-reload that repeats
 * on the next render — and unlike the recorded case there is nothing to stop
 * it. Declining hands the same recovery to the ErrorBoundary's manual Reload
 * button, which is user-paced and cannot loop.
 *
 * It must also not THROW. `sessionStorage` access raises SecurityError in a
 * webview with storage disabled, and this runs inside the `.catch` that
 * classifies the load failure: the SecurityError would replace the
 * ChunkLoadError in the rejection, so the boundary would see an error
 * `isChunkLoadError` does not recognise and log a genuine stale chunk as an
 * ordinary crash — defeating the classification this module exists to give it.
 */
function claimReloadAttempt(key: string, reloadCount: number): boolean {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(reloadCount + 1));
    return true;
  } catch {
    return false;
  }
}

/**
 * One-shot stale-chunk recovery shared by `lazyWithRetry` and the
 * `unhandledrejection` safety net in main.tsx. Returns true when a reload
 * was initiated (callers should stop rendering); false when the error is
 * not a chunk error, this chunk already had its attempt, or session storage
 * refused to record one. Never throws.
 */
export function attemptStaleChunkReload(error: unknown): boolean {
  if (!isChunkLoadError(error) || !(error instanceof Error)) return false;
  const key = chunkKeyFor(error);
  const reloadCount = staleChunkReloadCount();
  if (reloadCount >= MAX_RELOADS_PER_SESSION) return false;
  if (!claimReloadAttempt(key, reloadCount)) return false;
  // Best-effort observability: deploy-induced reload churn should be
  // visible in PostHog (may not flush before the reload; that's fine).
  // Lazy import keeps this module dependency-free for its many callers.
  void import('./analytics')
    .then(({ safeAnalytics }) => {
      safeAnalytics.trackImmediate(ANALYTICS_EVENTS.STALE_CHUNK_RELOADED, {
        trigger: 'automatic',
        chunk_key: key.slice(RELOAD_KEY_PREFIX.length, RELOAD_KEY_PREFIX.length + 200),
        reload_count: reloadCount + 1,
      });
    })
    .catch(() => undefined);
  globalThis.location.reload();
  return true;
}

/**
 * Detects stale-chunk errors thrown when a dynamic import fails
 * after a new deployment replaces the hashed asset files.
 *
 * The dynamic-import failure message differs per engine, and it also differs by
 * HOW the fetch failed, so all five variants must be matched or a whole browser
 * population never gets the reload:
 *  - Chromium: "Failed to fetch dynamically imported module: <url>"
 *  - Firefox:  "error loading dynamically imported module: <url>"
 *  - WebKit/Safari: "Importing a module script failed."
 *
 * The last two are the WRONG-MIME-TYPE pair, and they are not a hypothetical:
 * `firebase.json` rewrites `**` to `/index.html`, so a hashed chunk that no
 * longer exists after a deploy does not 404 — the CDN answers with index.html
 * as `text/html`. The engines report that as a MIME error, not as a failed
 * fetch:
 *  - WebKit: "'text/html' is not a valid JavaScript MIME type."
 *  - Chromium: "Failed to load module script: Expected a JavaScript-or-Wasm
 *    module script but the server responded with a MIME type of \"text/html\"."
 *
 * Missing the WebKit form crashed 3 iOS users 32 times over the 3 days to
 * 2026-08-19 (PostHog issue 019cd9cd…, all on `/m/*` routes): no reload fired,
 * the ErrorBoundary rendered, React re-rendered it, and one session produced 20
 * crashes in four minutes.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('is not a valid javascript mime type') ||
    msg.includes('failed to load module script') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('unable to preload css') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * A drop-in replacement for `React.lazy()` that handles stale-chunk errors
 * from new deployments by reloading the page once.
 *
 * After a deployment Vite produces new content-hashed filenames.
 * Users with a cached index.html still reference old chunks that no longer
 * exist on the server, causing "Failed to fetch dynamically imported module".
 * A CDN/browser request can also remain pending indefinitely without rejecting;
 * active route loads are bounded so that failure reaches the same recovery path.
 *
 * On failure this wrapper:
 *  1. Optionally bounds an active critical-route load at its caller-selected
 *     deadline. Default component loads and background preloads stay unbounded.
 *  2. Checks sessionStorage to avoid an infinite reload loop.
 *  3. If not already attempted, hard-reloads the page so the browser
 *     fetches the new index.html with correct chunk references.
 *  4. If a reload was already attempted, re-throws so the ErrorBoundary
 *     can display a user-friendly message.
 */
export interface LazyWithPreload<T extends AnyComponent> extends LazyExoticComponent<T> {
  /**
   * Warm this route's chunk ahead of navigation (fire-and-forget). Failures
   * are swallowed and un-memoized so the real render retries from scratch —
   * a failed hover/idle preload must never poison the route.
   */
  preload: () => void;
}

export interface LazyWithRetryOptions {
  /**
   * Bound an active render load. Opt in only for critical route chunks: this
   * recovery can hard-reload the SPA, which is unsafe for optional previews or
   * editors that may be loading while the user has unsaved interaction state.
   */
  activeLoadTimeoutMs?: number;
  /** Stable route identity for the per-chunk one-shot reload guard. */
  reloadKey?: string;
}

export function lazyWithRetry<T extends AnyComponent>(
  factory: () => Promise<{ default: T }>,
  options: LazyWithRetryOptions = {},
): LazyWithPreload<T> {
  // Memoize the in-flight/settled load so preload() and React's own lazy
  // resolution share one dynamic import instead of racing two.
  let loadPromise: Promise<{ default: T }> | null = null;

  const load = (): Promise<{ default: T }> => {
    loadPromise ??= factory().catch((error: unknown) => {
      // Un-memoize so a later render (or preload) retries the import
      // rather than replaying this cached rejection.
      loadPromise = null;
      throw error;
    });
    return loadPromise;
  };

  const loadWithActiveTimeout = (): Promise<{ default: T }> => {
    if (options.activeLoadTimeoutMs === undefined) return load();

    return new Promise<{ default: T }>((resolve, reject) => {
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        const suffix = options.reloadKey ? ` (${options.reloadKey})` : '';
        const error = new Error(`Timed out while loading a lazy route chunk${suffix}.`);
        error.name = 'ChunkLoadError';
        reject(error);
      }, options.activeLoadTimeoutMs);

      load().then(
        (module) => {
          if (timedOut) return;
          clearTimeout(timeout);
          resolve(module);
        },
        (error: unknown) => {
          if (timedOut) return;
          clearTimeout(timeout);
          reject(error);
        },
      );
    });
  };

  const loadForActiveComponent = (): Promise<{ default: T }> =>
    loadWithActiveTimeout().catch((error: unknown) => {
      if (attemptStaleChunkReload(error)) {
        // Never-resolving promise so React doesn't try to render
        // while the page is reloading.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    });

  const Component = lazy(loadForActiveComponent) as LazyWithPreload<T>;
  Component.preload = () => {
    load().catch(() => {
      // Fire-and-forget: preload failures are non-events; the real
      // navigation will retry and surface errors through the boundary.
    });
  };
  return Component;
}
